/* ────────────────────────────────────────────────────────────────
   sale-schemas.ts   •  Zod schemas & runtime types for a Sale
   Place inside your types package (e.g. @am3nd-org/pointcraft-types)
   or local src/types folder.
   ──────────────────────────────────────────────────────────────── */
import { z } from 'zod';
import { EmployeeSchema } from '../employee';
import { MobileServiceSchema } from '../mobile-services';
import { ProductDataSchema, ProductTypeSchema } from '../products';
import { SelectedModifierDataSchema } from '../products/modifiers';
import { SourceStatusEnum, SourceStatusSchema } from '../status';
import { ZodStringToDate } from '../system';
import { ProcessingConfigSchema } from '../user/web';
import { BillingControlsSchema, InvoiceControlsSchema, NotesSchema, OrderSchema, SaleCustomerSchema, SalesChannelSchema, } from './meta';
import { SalePaymentSchema } from './payment';
import { SaleOrderDetailsSchema } from './order-details';
export * from './order-details';
// Re-export v4 sale schemas and types
export * from './v4';
// Re-export meta schemas for convenience
export { BillingControlsSchema, InvoiceControlsSchema, NotesSchema, OrderCustomerSchema, OrderSchema, SaleCustomerSchema, } from './meta';
// Re-export payment schemas and utilities
export { BasePaymentTxSchema, calculateNetPaymentAmount, calculateTotalAmount, calculateTotalRefunded, CardPaymentDetailsSchema, CardPaymentTxSchema, CompletedPaymentTxSchema, createPaymentTx, FailedPaymentTxSchema, getAllCardPayments, getPaymentBreakdown, isCardPayment, NonCardPaymentTxSchema, PaymentFailureDetailsSchema, PendingPaymentTxSchema, RefundDetailsSchema, RefundedPaymentTxSchema, SalePaymentSchema, } from './payment';
export const SaleItemMetadataSchema = z.object({
    selectedModifierData: SelectedModifierDataSchema,
    isTaxable: z.boolean().optional().default(true),
    ebtEligible: z.boolean().optional().default(false),
    oneTouchGroupId: z.string().optional(),
    oneTouchGroupName: z.string().optional(),
    specialInstructions: z.string().optional(),
});
export const SalesItemSchema = z.object({
    id: z.string(),
    identifier: z.string(),
    name: z.string(),
    price: z.number(),
    cost: z.number(),
    quantity: z.number(),
    linkedProduct: ProductDataSchema.nullable(),
    linkedMobileService: MobileServiceSchema.nullable(),
    productType: ProductTypeSchema.nullable(),
    metadata: SaleItemMetadataSchema,
});
/* ── 2 ▸ Meta (tracing information) ───────────────────────────── */
/**
 * SaleMetaSchema - Revised architecture with grouped controls
 *
 * Major changes from previous version:
 * - Removed: taxEnabled, processingFeesEnabled (moved to billingControls)
 * - Added: billingControls, invoiceControls, notes (grouped structures)
 * - Added: salesChannel enum (POS, INVOICE, MOBILE_SERVICE, WEB_PAYMENT, RECURRING_PAYMENT, PAYMENT_LINK)
 * - Added: order object with full customer info
 * - Added: syncedToQuickBooks flag
 * - Updated: customer now supports full customer data (id, name required; email, phone, address, debtTracker optional)
 */
export const SaleMetaSchema = z.object({
    shortIdentifier: z.string(),
    billingControls: BillingControlsSchema,
    invoiceControls: InvoiceControlsSchema,
    notes: NotesSchema,
    salesChannel: SalesChannelSchema,
    /** @deprecated Use sale.orderDetails. Compatibility output only. */
    order: OrderSchema.describe('@deprecated Use sale.orderDetails'),
    syncedToQuickBooks: z.boolean().default(false),
    employee: EmployeeSchema.nullable(),
    customer: SaleCustomerSchema,
    businessLocation: z.object({ id: z.string(), name: z.string() }),
    createdAt: z.date().or(ZodStringToDate),
    updatedAt: z.date().or(ZodStringToDate),
});
/* ── 3 ▸ Status flags ─────────────────────────────────────────── */
export const SaleStatusSchema = z.object({
    hasBeenCreated: z.boolean().default(false),
    isCompleted: z.boolean().default(false),
    isReadyToPay: z.boolean().default(false),
    currentSaleStatus: SourceStatusSchema.default(SourceStatusEnum.UNPAID),
});
/* ── 4 ▸ Financial block (auto-computed) ──────────────────────── */
/**
 * SaleFinancialSchema - Financial information for the sale
 *
 * Changes from previous version:
 * - Added: discount (was missing)
 * - Added: shippingCharges (was missing)
 * - Added: processingConfig (renamed from copyOfProcessingConfig)
 */
export const SaleFinancialSchema = z.object({
    // Core amounts
    total: z.number(),
    cost: z.number(),
    subTotal: z.number(),
    balanceDue: z.number(),
    // Fees and adjustments
    processingFees: z.number(),
    merchantProcessingFees: z.number(),
    adjustment: z.number(),
    // Tax and tip
    taxes: z.number(),
    tip: z.number(),
    // Additional charges
    discount: z.number(),
    shippingCharges: z.number(),
    // Audit trail - processing config snapshot
    processingConfig: ProcessingConfigSchema,
});
/* ── 5 ▸ Master Sale schema ───────────────────────────────────── */
/**
 * SaleSchema - The complete sale record
 *
 * Changes from previous version:
 * - Renamed: meta → salesMeta (for clarity)
 * - Added: data array for historical snapshots (uses lazy schema for circular reference)
 */
// Define the base schema without the recursive data field
const BaseSaleSchema = z.object({
    id: z.string(),
    items: SalesItemSchema.array(),
    salesMeta: SaleMetaSchema,
    status: SaleStatusSchema,
    financialInfo: SaleFinancialSchema,
    paymentInfo: SalePaymentSchema,
    orderDetails: SaleOrderDetailsSchema.nullable().optional(),
});
// Create the full schema with recursive data field, properly typed
export const SaleSchema = BaseSaleSchema.extend({
    data: z
        .array(z.lazy(() => SaleSchema))
        .optional()
        .default([]),
});
